// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// The per-goal data-gap rule the engine (functions/CareRx/CareRxAiEngines.cs, DataGapEngine) applies.
// The server derives it from the goal's own fields and ignores client flags; this copy only tells the
// pharmacist before they click. One rule, two languages — keep them identical.
//
// ASP 2026-10-11 (#12): "can this not be a hard halt — it needs a baseline for all the suggested care plan".
// BINARY (yes/no) goals need no baseline — "care plan reviewed on cadence" has no starting number.
// ASP 2026-10-28 (#14): neither do goals that are not measured by a lab value. The suggested Adherence
// goal is "up, 90 %", so the binary-only exemption still hard-stopped it. The discriminator is the
// goal's DOMAIN (the goal model has no analyte key; `measure` is free text): adherence and
// self-management are never lab values. Clinical and safety goals can be, so they keep the rule.
export const NON_LAB_DOMAINS: readonly string[] = ['adherence', 'self_management']

export interface GoalGapFields { direction?: string | null; domain?: string | null; baseline_value?: string | number | null }

export function isBaselineExempt(g: Pick<GoalGapFields, 'direction' | 'domain'>): boolean {
  return (g.direction ?? '').trim().toLowerCase() === 'binary'
    || NON_LAB_DOMAINS.includes((g.domain ?? '').trim().toLowerCase())
}

export function hasBaseline(g: Pick<GoalGapFields, 'baseline_value'>): boolean {
  return g.baseline_value !== undefined && g.baseline_value !== null && String(g.baseline_value).trim() !== ''
}

/** Mirrors DataGapEngine.Evaluate over DataGapEngine.ForGoal: a gap = not exempt and no baseline. */
export function hasDataGap(g: GoalGapFields): boolean {
  return !isBaselineExempt(g) && !hasBaseline(g)
}

/**
 * ASP #14 follow-up: a SAVED baseline waiver carries into the next version only while the goal's measure
 * and direction are unchanged — trimmed, case-insensitive, null = blank. Any other edit keeps it.
 * MUST match CarePlanWaivers.Unchanged in functions/CareRx/CareRxAiEngines.cs.
 */
export function carryWaiver(prior: { measure?: string | null; direction?: string | null },
                            now: { measure?: string | null; direction?: string | null }): boolean {
  const n = (v?: string | null) => (v ?? '').trim().toLowerCase()
  return n(prior.measure) === n(now.measure) && n(prior.direction) === n(now.direction)
}
